import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { segurosApi } from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';

const fmtCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

// Iter 368 — Cuenta contable de los egresos de Seguros (UF) + auditoría/recategorización en Alegra
export function SegurosConfigDialog({ open, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState('');
  const [egresos, setEgresos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recat, setRecat] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([segurosApi.getConfig(), segurosApi.listarEgresos()]);
      setCfg(c.data); setSel(String(c.data?.cuenta_egreso?.id || ''));
      setEgresos(e.data?.egresos || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar la configuración');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) cargar(); }, [open]);

  const guardar = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      const r = await segurosApi.setConfig({ cuenta_egreso_id: Number(sel) });
      toast.success(`Cuenta de egresos: ${r.data.cuenta_egreso.code} – ${r.data.cuenta_egreso.name}`);
      await cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const pendientes = (egresos || []).filter((e) => e.requiere_recategorizar);

  const recategorizar = async () => {
    if (!pendientes.length) return;
    if (!window.confirm(`Se cambiará la cuenta contable de ${pendientes.length} egreso(s) en Alegra a ${cfg?.cuenta_egreso?.code} – ${cfg?.cuenta_egreso?.name}. Fecha, monto, proveedor y N° se mantienen. ¿Continuar?`)) return;
    setRecat(true);
    try {
      const r = await segurosApi.recategorizarEgresos({ dry_run: false, payment_ids: pendientes.map((e) => e.payment_id) });
      const s = r.data.resumen || {};
      const errores = (r.data.resultados || []).filter((x) => x.accion === 'error');
      if (errores.length) {
        toast.warning(`${s.recategorizado || 0} recategorizado(s) · ${errores.length} con error: ${errores.map((x) => `${x.numero_egreso}: ${x.motivo}`).join(' | ')}`, { duration: 12000 });
      } else {
        toast.success(`${s.recategorizado || 0} egreso(s) recategorizado(s) en Alegra`);
      }
      await cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al recategorizar');
    } finally { setRecat(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dialog-seguros-config">
        <DialogHeader><DialogTitle>Seguros (UF) — Cuenta contable de los egresos</DialogTitle></DialogHeader>
        {loading && !cfg ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> : cfg && (
          <div className="space-y-4 text-sm">
            <div className="rounded border bg-slate-50 p-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                El egreso de la prima es un <b>anticipo</b>: se registra en esta cuenta y el gasto se reconoce después, al aplicar el anticipo a la factura de la aseguradora (Contabilidad → Anticipos).
              </p>
              <div className="flex items-center gap-2">
                <select value={sel} onChange={(e) => setSel(e.target.value)} className="flex-1 h-9 px-2 rounded-md border text-sm" data-testid="select-cuenta-egreso-seguros">
                  {(cfg.cuentas || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.code} – {c.name} ({c.type})</option>
                  ))}
                </select>
                <Button size="sm" onClick={guardar} disabled={saving || String(cfg.cuenta_egreso?.id) === sel} data-testid="btn-guardar-cuenta-egreso">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
                </Button>
              </div>
              <div className="text-xs" data-testid="cuenta-egreso-actual">
                Cuenta actual: <Badge variant="outline">{cfg.cuenta_egreso?.code} – {cfg.cuenta_egreso?.name}</Badge>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Egresos generados desde Seguros (UF) <span className="text-muted-foreground font-normal">({(egresos || []).length})</span></h4>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={cargar} disabled={loading} data-testid="btn-refrescar-egresos-seguros">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button size="sm" onClick={recategorizar} disabled={recat || !pendientes.length} data-testid="btn-recategorizar-egresos">
                  {recat ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                  Recategorizar {pendientes.length ? `(${pendientes.length})` : ''}
                </Button>
              </div>
            </div>
            <div className="border rounded max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">N° Alegra</th><th className="text-left px-2 py-1">Fecha</th>
                    <th className="text-left px-2 py-1">Aseguradora</th><th className="text-right px-2 py-1">Monto</th>
                    <th className="text-left px-2 py-1">Cuenta en Alegra</th><th className="text-left px-2 py-1">Cuotas</th><th className="px-2 py-1">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {(egresos || []).map((e) => (
                    <tr key={e.payment_id} className="border-t" data-testid={`egreso-row-${e.numero_egreso}`}>
                      <td className="px-2 py-1 font-mono">{e.alegra?.numero || e.numero_egreso}</td>
                      <td className="px-2 py-1">{fmtFecha(e.alegra?.date || e.fecha_pago)}</td>
                      <td className="px-2 py-1">{e.aseguradora}</td>
                      <td className="px-2 py-1 text-right">{fmtCLP(e.alegra?.amount ?? e.total_erp)}</td>
                      <td className="px-2 py-1">{e.alegra?.error ? <span className="text-red-600">{e.alegra.error}</span> : (e.cuentas_alegra || []).map((c) => c.name).join(', ')}</td>
                      <td className="px-2 py-1 text-muted-foreground">{e.cuotas.join(', ')}</td>
                      <td className="px-2 py-1 text-center">
                        {e.requiere_recategorizar === false && <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />}
                        {e.requiere_recategorizar && <AlertTriangle className="h-4 w-4 text-amber-600 inline" />}
                        {e.alegra?.bills > 0 && <Badge variant="outline" className="ml-1">aplicado</Badge>}
                      </td>
                    </tr>
                  ))}
                  {egresos && egresos.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-center text-muted-foreground">Sin egresos generados.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="btn-cerrar-seguros-config">Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
